import {
  Interval,
  ResolvedTransition,
  TransitionFunction,
} from '@/types/variable'
import { transitionNinetyFivePercentValue } from '@/utils/network/transitionFunctions'

import { assertNodeIdentityContract } from './assertNodeIdentityContract'
import addTypesToVariables from '../../conversions/addTypeToVariables'
import { showError } from '../../toast'
import { trpc } from '../../trpc'

// What may be written to `network.json` for one node, or `undefined` for
// "inherit the network default".
//
// KBE-837: a backstop against history, not a live gap. The invariant — one config
// per boundary, so `length === intervals.length - 1` — is enforced on every write
// path: `setTransitionFunctions` validates the length; `setVariableIntervals`
// asserts one interval per state and rebuilds in canonical state order, so the
// interval count cannot drift from the state count; every mutation that changes
// the state count (`createVariableState`, `deleteState`, `reorderVariableStates`,
// `changeToDiscrete`) clears or realigns the column; both merge writers bail on a
// length mismatch rather than write one; and import rejects a wrong-length array
// outright, normalizing `[]` to null so `changeToSemiContinuous` cannot inflate an
// empty array into a stale one.
//
// What is NOT enforced is Postgres itself —
// `variable_transition_points_bracket_midpoint` constrains one entry's own fields
// and nothing more. So the shapes this filter drops (wrong length, empty, a `null`
// hole, an entry missing a point) are reachable only by a row that predates the
// guards above, since V136 and V153 converted rather than cleared, or by a
// hand-written `UPDATE`. All of them make `@ancestralize/bayesian-utils` refuse the
// file, and the exposed party is whoever READS the export (Ancestra Health) — KBE
// absorbs a stale-length array as "inherit" in `resolveTransitionFunctions`.
// Exporting `undefined` says "inherit" explicitly, so a consumer inherits where KBE
// inherits.
//
// KBE-839 considered a DB constraint that would have made three of these four
// shapes impossible to store, and rejected it (a CHECK cannot be DEFERRABLE, so it broke
// every writer that changes the state count across statements). So this filter
// stays a live guard rather than a backstop: nothing below the write paths enforces
// the LENGTH, and this is the only thing standing between a legacy or hand-written
// row and a consumer that refuses the file.
//
// KBE-858 gave it a second job: the export must MIRROR a Symmetrical entry, because
// `parseExportedNetwork` requires all three points on every stored entry and would
// reject the narrower form outright. Symmetrical/skewed is KBE's authoring
// vocabulary and stops at this boundary -- so what ships is always the whole curve.
//
// Entries are inspected as `unknown` deliberately: the declared type says they are
// `TransitionFunction`, and the whole point is that the database can disagree.
export function exportableTransitionFunctions(
  transitionFunctions: TransitionFunction[] | null | undefined,
  intervals: readonly Interval[] | null | undefined,
): ResolvedTransition[] | undefined {
  const boundaryCount = (intervals?.length ?? 0) - 1
  if (!transitionFunctions || boundaryCount < 1) return undefined
  if (transitionFunctions.length !== boundaryCount) return undefined

  const exportable: ResolvedTransition[] = []
  for (const entry of transitionFunctions) {
    const candidate: unknown = entry
    if (typeof candidate !== 'object' || candidate === null) return undefined
    const { midpoint, fivePercentValue, ninetyFivePercentValue } =
      candidate as Partial<TransitionFunction>
    if (!Number.isFinite(midpoint) || !Number.isFinite(fivePercentValue)) {
      return undefined
    }
    if (
      ninetyFivePercentValue !== null &&
      ninetyFivePercentValue !== undefined &&
      !Number.isFinite(ninetyFivePercentValue)
    ) {
      return undefined
    }
    exportable.push({
      midpoint: midpoint as number,
      fivePercentValue: fivePercentValue as number,
      ninetyFivePercentValue: transitionNinetyFivePercentValue({
        midpoint: midpoint as number,
        fivePercentValue: fivePercentValue as number,
        ninetyFivePercentValue,
      }),
    })
  }
  return exportable
}

export async function getExportedNetwork(
  trpcUtils: ReturnType<typeof trpc.useUtils>,
  networkId: string,
) {
  // Exports must reflect the current network state, so bypass the app-wide
  // `staleTime: Infinity` cache on every fetch below rather than risk handing
  // back a cached snapshot from before the latest edit.
  const network = await trpcUtils.network.getNetwork
    .fetch({ networkId: networkId }, { staleTime: 0 })
    .catch((e) => {
      showError('Failed to export network')

      if (e instanceof Error) {
        throw e
      }
      throw new Error('Failed to fetch network')
    })

  const sexRelatedTags =
    await trpcUtils.network.getSexRelatedTagsInNetwork.fetch(
      { networkId },
      { staleTime: 0 },
    )

  const variableStates = await trpcUtils.network.getNetworkVariableStates
    .fetch({ networkId }, { staleTime: 0 })
    .catch((e) => {
      showError('Failed to fetch variable states')
      if (e instanceof Error) {
        throw e
      }
      throw new Error('Failed to fetch variable states')
    })

  const stateIdToNameMap = new Map(
    variableStates.map((state) => [state.id, state.name]),
  )

  const variablesWithType = addTypesToVariables(
    network.variables,
    network.variableTypes,
  )
  const sortedVariables = [...variablesWithType].sort((a, b) =>
    a.name.trim().localeCompare(b.name.trim()),
  )
  const variableMap = new Map(network.variables.map((v) => [v.id, v.name]))
  const variablesWithTags: Array<
    (typeof variablesWithType)[number] & { tags: string[] }
  > = []

  const nodes = Object.fromEntries(
    sortedVariables.map((v) => {
      const inboundArrows = v.inboundArrows.map((a) => {
        const name = variableMap.get(a.variableId)
        return name
      })
      const tags = [...(v.tags ?? [])]
      if (sexRelatedTags[v.id]) {
        tags.push(sexRelatedTags[v.id])
      }
      variablesWithTags.push({ ...v, tags })
      const variable = {
        parameterizationType: v.parameterizationType,
        comment: v.comment,
        tags,
        definition: v.definition ?? null,
        references: v.references ?? null,
        formula: v.formula ?? null,
        prevalenceExplanation: v.prevalenceExplanation ?? null,
        states: v.stateIds.map((stateId) => {
          const stateName = stateIdToNameMap.get(stateId)
          if (!stateName) {
            throw new Error(
              `State ID not found in mapping: ${stateId} for variable: ${v.name}`,
            )
          }
          return stateName
        }),
        parents: inboundArrows,
        arrowComments: v.inboundArrows.map((a) => a.comment),
        probabilities: v.probabilities,
        faults:
          !v.faults || v.faults.length === 0
            ? undefined
            : v.faults.map((f) => {
                const stateName = stateIdToNameMap.get(f)
                if (!stateName) {
                  throw new Error(
                    `State ID not found in mapping: ${f} for variable: ${v.name}`,
                  )
                }
                return stateName
              }),
        type: v.type.name,
        intervals: v.intervals ?? null,
        intervalUnit: v.intervalUnit ?? null,
        // KBE-758. Exported unresolved, and OMITTED when the node has no
        // override of its own (`undefined` is dropped by JSON.stringify, same
        // as `faults` above) — an absent key means "inherit
        // `defaultTransitionHalfBandPercent`", which is the majority of nodes,
        // so emitting an explicit null for each of them would be pure noise.
        // Baking in each node's resolved curve instead would export numbers no
        // researcher ever set and freeze every inheriting node against a
        // default that is meant to move.
        //
        // KBE-858: each entry states its three ABSOLUTE points, mirrored at
        // export time so `ninetyFivePercentValue` is always present -- the
        // library requires all three and rejects a node missing any. A consumer
        // needs no `intervals` to read the curve, and the `midpoint` is the
        // crossover, which may deliberately sit away from the interval bound.
        //
        // One case remains where KBE does NOT use these values as written, so a
        // consumer must apply the same fallback to agree with our Diagnosis page
        // (`resolveTransitionFunctions`): an array whose length is not
        // `intervals.length - 1` is discarded wholesale, the node inheriting
        // throughout. The per-boundary fallback that used to sit beside it is
        // gone — a distance cannot be stranded.
        //
        // KBE-837 makes that sentence true. It described the intent, but the
        // export emitted whatever was stored whenever it was non-empty, so a
        // corrupt array could reach a consumer that throws on it —
        // `exportableTransitionFunctions` is where the discard actually happens.
        transitionFunctions: exportableTransitionFunctions(
          v.transitionFunctions,
          v.intervals,
        ),
        optimalState: v.optimalStateId
          ? (() => {
              const stateName = stateIdToNameMap.get(v.optimalStateId)
              if (!stateName) {
                throw new Error(
                  `Optimal state ID not found in mapping: ${v.optimalStateId} for variable: ${v.name}`,
                )
              }
              return stateName
            })()
          : null,
        copyReferenceId: v.copyReferenceId,
      }
      return [v.name.trim(), variable]
    }),
  )
  assertNodeIdentityContract(nodes)
  const sortedTypes = [...network.variableTypes].sort((a, b) =>
    a.name.localeCompare(b.name),
  )
  const types = Object.fromEntries(
    sortedTypes.map((vt) => [
      vt.name,
      {
        diagnosticType: vt.diagnosticType,
        color: vt.color,
        upstreamEffectsDisabled: vt.upstreamEffectsDisabled,
      },
    ]),
  )
  // The network default comes first so the small metadata sits above the bulk
  // `nodes` blob when a researcher opens the file. It is required, not
  // optional: without it a node with `transitionFunctions: null` is
  // uninterpretable, since null is "inherit" rather than "off".
  return {
    defaultTransitionHalfBandPercent: network.defaultTransitionHalfBandPercent,
    nodes,
    types,
  }
}
